import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { isValidEmail, } from "@diality/contracts";
import { cn } from "../lib/utils";
import { BlockLink, useBlockRender } from "../render/context";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Section } from "./_section";
/** Visitor feedback reports forwarding, not membership in a stored mailing list. */
const SIGNUP_MESSAGES = {
    subscribed: {
        text: "Your signup request has been received.",
        tone: "confirm",
    },
    "invalid-email": {
        text: "Please enter a valid email address.",
        tone: "problem",
    },
    unavailable: {
        text: "We couldn't sign you up just now. Please try again in a moment.",
        tone: "problem",
    },
};
/**
 * The email-capture form inside a CtaBlock — the visitor joining the mailing
 * list.
 *
 * With the `submitMailingListSignup` seam injected (public site) a submitted
 * address is filed with the platform and the visitor is told what became of it;
 * without it (CMS canvas, Preview, Storybook) the panel confirms locally, so the
 * same markup previews cleanly in the editor without subscribing anyone.
 *
 * Format guidance uses the shared `isValidEmail` rule the subscribers Lambda
 * applies, so the inline message and the server's answer can never disagree about
 * what a usable address is. The server remains the authority.
 */
function EmailCaptureForm({ data }) {
    const { submitMailingListSignup } = useBlockRender();
    const fieldId = React.useId();
    const [result, setResult] = React.useState(null);
    const join = async (event) => {
        event.preventDefault();
        // Read the form BEFORE any await — currentTarget is nulled once we yield.
        const form = event.currentTarget;
        const address = String(new FormData(form).get("email") ?? "").trim();
        if (!isValidEmail(address)) {
            setResult("invalid-email");
            return;
        }
        if (!submitMailingListSignup) {
            // Preview surfaces (CMS canvas, Preview, Storybook): confirm without joining.
            setResult("subscribed");
            form.reset();
            return;
        }
        // The seam is public API of this package, so a rejected promise is treated as
        // "we don't know" — never as a confirmation of a subscription we can't prove.
        let outcome;
        try {
            outcome = await submitMailingListSignup(address);
        }
        catch {
            outcome = "unavailable";
        }
        setResult(outcome);
        if (outcome === "subscribed") {
            form.reset();
        }
    };
    const message = result ? SIGNUP_MESSAGES[result] : null;
    return (_jsxs("div", { className: "w-full", children: [_jsxs("form", { noValidate: true, onSubmit: (event) => void join(event), className: "flex w-full flex-col gap-2 @min-[640px]:flex-row", children: [_jsx("label", { htmlFor: fieldId, className: "sr-only", children: "Email address" }), _jsx(Input, { id: fieldId, name: "email", type: "email", autoComplete: "email", placeholder: data.placeholder ?? "Enter your email", className: "h-11 flex-1" }), _jsx(Button, { type: "submit", children: data.submitLabel ?? "Sign up" })] }), _jsx("p", { role: "status", "aria-live": "polite", className: cn("mt-3 text-sm", message?.tone === "problem"
                    ? "text-destructive"
                    : "text-muted-foreground"), children: message?.text ?? "" })] }));
}
/**
 * CtaBlock — data-driven call-to-action / newsletter panel.
 *
 * Net-new, serializable contract (see @diality/contracts CtaBlockData). Rendered
 * identically by the CMS canvas and the public site. When emailCapture is on the
 * panel shows a live mailing-list signup form (see {@link EmailCaptureForm});
 * otherwise it shows a single CTA link via BlockRenderProvider.
 */
export function CtaBlock({ data }) {
    const { heading, body, cta, emailCapture } = data;
    return (_jsx(Section, { children: _jsx("div", { className: "mx-auto max-w-2xl px-4 @min-[640px]:px-6 @min-[1024px]:px-8", children: _jsx("div", { className: "rounded-xl border border-border bg-card p-8 shadow-card @min-[640px]:p-10", children: _jsxs("div", { className: "flex flex-col items-center gap-4 text-center", children: [_jsx("h2", { className: "text-xl font-bold @min-[640px]:text-2xl", children: heading }), body && _jsx("p", { className: "text-muted-foreground", children: body }), emailCapture ? (_jsx(EmailCaptureForm, { data: data })) : (cta && (_jsx("div", { className: "pt-2", children: _jsx(Button, { asChild: true, children: _jsx(BlockLink, { href: cta.href, children: cta.label }) }) })))] }) }) }) }));
}
export function ctaFromBlock(block, _resolve) {
    const s = block.scalars ?? {};
    return {
        heading: s.heading || "",
        body: s.body || undefined,
        emailCapture: s.emailCapture === "true",
        cta: s.ctaLabel ? { label: s.ctaLabel, href: s.ctaHref || "#" } : undefined,
        submitLabel: s.submitLabel || undefined,
        placeholder: s.placeholder || undefined,
    };
}
export const ctaBlockEntry = {
    Component: CtaBlock,
    fromBlock: ctaFromBlock,
};
//# sourceMappingURL=cta.js.map